import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { usersAPI } from '../services/api';

const Admin = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm();

  const loadUsers = async () => {
    try {
      setLoading(true);
      const res = await usersAPI.list();
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Error loading users:', e);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const onCreate = async (data) => {
    try {
      await usersAPI.create({ email: data.email, password: data.password, is_admin: data.is_admin || false });
      toast.success('User created');
      reset();
      loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to create user');
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await usersAPI.delete(id);
      toast.success('User deleted');
      loadUsers();
    } catch (e) {
      toast.error('Failed to delete user');
    }
  };

  const onReset = async (id) => {
    const newPass = window.prompt('Enter new password');
    if (!newPass) return;
    try {
      await usersAPI.resetPassword(id, newPass);
      toast.success('Password reset');
    } catch (e) {
      toast.error('Failed to reset password');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Admin</h1>
        <p className="text-gray-600 mt-2">Manage users</p>
      </div>

      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Create User</h2>
        <form onSubmit={handleSubmit(onCreate)} className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="label">Email</label>
            <input type="email" className="input" placeholder="user@example.com" {...register('email', { required: 'Email is required' })} />
            {errors.email && <p className="text-red-600 text-sm mt-1">{errors.email.message}</p>}
          </div>
          <div>
            <label className="label">Password</label>
            <input type="password" className="input" placeholder="••••••••" {...register('password', { required: 'Password is required' })} />
            {errors.password && <p className="text-red-600 text-sm mt-1">{errors.password.message}</p>}
          </div>
          <div className="flex items-end">
            <label className="inline-flex items-center space-x-2">
              <input type="checkbox" className="h-4 w-4" {...register('is_admin')} />
              <span className="text-sm text-gray-700">Admin</span>
            </label>
          </div>
          <div className="md:col-span-3">
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Creating…' : 'Create User'}
            </button>
          </div>
        </form>
      </div>

      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Users</h2>
        {loading ? (
          <div className="flex items-center justify-center h-24">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-600">
                  <th className="p-2">Email</th>
                  <th className="p-2">Admin</th>
                  <th className="p-2">Created</th>
                  <th className="p-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} className="border-t">
                    <td className="p-2">{u.email}</td>
                    <td className="p-2">{u.is_admin ? 'Yes' : 'No'}</td>
                    <td className="p-2">{u.created_at ? new Date(u.created_at).toLocaleString() : '—'}</td>
                    <td className="p-2 text-right space-x-2">
                      <button className="btn btn-sm btn-secondary" onClick={() => onReset(u.id)}>Reset Password</button>
                      <button className="btn btn-sm btn-danger" onClick={() => onDelete(u.id)}>Delete</button>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td className="p-2 text-gray-500" colSpan={4}>No users</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;